import React from "react";
import "./HTML_MAIN_CSS.css";

const HTMLCSS = () => {
  return (
    <div className="html-container">
      <h1>HTML – CSS (Cascading Style Sheets)</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction to CSS</h2>
        <p>
          <strong>CSS (Cascading Style Sheets)</strong> is the styling language used to describe
          the presentation of HTML elements on a webpage. While HTML defines the structure and
          content, CSS controls the layout, colors, fonts, and overall appearance.
        </p>
        <p>
          CSS makes websites visually appealing and consistent across multiple pages. It separates
          content (HTML) from design (CSS), which improves maintainability and scalability.
        </p>
      </section>

      {/* 2. Why CSS is Needed */}
      <section id="why-css">
        <h2>2. Why CSS is Important</h2>
        <ul>
          <li>Improves the design and readability of web pages.</li>
          <li>Allows consistent styling across multiple pages.</li>
          <li>Enables responsive design for mobile, tablet, and desktop.</li>
          <li>Reduces code duplication by separating style from content.</li>
          <li>Provides flexibility for animations, transitions, and effects.</li>
        </ul>
      </section>

      {/* 3. Syntax */}
      <section id="syntax">
        <h2>3. CSS Syntax</h2>
        <p>CSS follows a simple rule-based syntax consisting of a selector and a declaration block.</p>
        <pre>
          <code>{`selector {
  property: value;
}`}</code>
        </pre>
        <p>Example:</p>
        <pre>
          <code>{`p {
  color: blue;
  font-size: 18px;
}`}</code>
        </pre>
        <p>
          Here, the selector <code>p</code> targets all paragraph elements and applies blue text
          with a font size of 18px.
        </p>
      </section>

      {/* 4. Types of CSS */}
      <section id="types-of-css">
        <h2>4. Types of CSS Integration in HTML</h2>
        <p>
          CSS can be applied to HTML in three main ways: <strong>Inline</strong>,{" "}
          <strong>Internal</strong>, and <strong>External</strong>.
        </p>

        <h3>1️⃣ Inline CSS</h3>
        <p>CSS is applied directly inside the HTML element using the <code>style</code> attribute.</p>
        <pre>
          <code>{`<p style="color: red; font-size: 20px;">This is inline styled text.</p>`}</code>
        </pre>

        <h3>2️⃣ Internal CSS</h3>
        <p>CSS rules are defined inside a <code>&lt;style&gt;</code> tag within the HTML document’s head.</p>
        <pre>
          <code>{`<head>
  <style>
    h1 {
      color: green;
      text-align: center;
    }
  </style>
</head>`}</code>
        </pre>

        <h3>3️⃣ External CSS</h3>
        <p>CSS is written in a separate <code>.css</code> file and linked to the HTML document.</p>
        <pre>
          <code>{`<head>
  <link rel="stylesheet" href="styles.css">
</head>`}</code>
        </pre>
      </section>

      {/* 5. CSS Selectors */}
      <section id="selectors">
        <h2>5. CSS Selectors</h2>
        <p>Selectors define which HTML elements the style rules will apply to.</p>
        <pre>
          <code>{`/* Element Selector */
p { color: red; }

/* Class Selector */
.intro { font-size: 18px; }

/* ID Selector */
#main-title { font-weight: bold; }

/* Grouping */
h1, h2, h3 { text-align: center; }`}</code>
        </pre>
      </section>

      {/* 6. CSS Colors, Fonts, and Background */}
      <section id="styling-basics">
        <h2>6. Styling Basics (Colors, Fonts, and Backgrounds)</h2>
        <p>Common properties to style text and elements include:</p>
        <pre>
          <code>{`body {
  background-color: #f0f0f0;
  color: #333;
  font-family: "Segoe UI", sans-serif;
}

h1 {
  color: darkblue;
}

p {
  background-color: lightyellow;
  padding: 10px;
}`}</code>
        </pre>
      </section>

      {/* 7. Cascading and Specificity */}
      <section id="cascading">
        <h2>7. Cascading and Specificity</h2>
        <p>
          The word <strong>"Cascading"</strong> in CSS means that styles can fall (or "cascade")
          from multiple sources. The browser decides which style to apply using the concept of
          <strong> specificity</strong>.
        </p>
        <ul>
          <li>Inline styles have the highest priority.</li>
          <li>ID selectors override class or element selectors.</li>
          <li>Later rules override earlier ones if they have the same specificity.</li>
        </ul>
      </section>

      {/* 8. Comments in CSS */}
      <section id="comments">
        <h2>8. Comments in CSS</h2>
        <p>
          CSS comments are ignored by the browser and help document your code.
        </p>
        <pre>
          <code>{`/* This is a CSS comment */
p {
  color: green; /* Text color */
}`}</code>
        </pre>
      </section>

      {/* 9. Example Project */}
      <section id="example-project">
        <h2>9. Example Project: HTML + CSS Integration</h2>
        <pre>
          <code>{`<!DOCTYPE html>
<html>
  <head>
    <link rel="stylesheet" href="style.css">
  </head>
  <body>
    <h1 class="main-heading">Welcome to CSS World</h1>
    <p>This page demonstrates the use of external CSS.</p>
  </body>
</html>

/* style.css */
body {
  background-color: #e6f7ff;
  font-family: Arial, sans-serif;
}
.main-heading {
  color: #0073e6;
  text-align: center;
}`}</code>
        </pre>
      </section>

      {/* 10. Problem Statement */}
      <section id="problem-statement">
        <h2>10. Problem Statement Example</h2>
        <p>
          <strong>Scenario:</strong> A website’s HTML structure is ready, but the page looks plain
          and inconsistent.
        </p>
        <p>
          <strong>Problem:</strong> Without CSS, maintaining style consistency and visual appeal
          across multiple pages is difficult.
        </p>
        <p>
          <strong>Solution:</strong> Use external CSS for consistent styling, responsive layouts,
          and easy updates across all web pages.
        </p>
      </section>

      {/* 11. Best Practices */}
      <section id="best-practices">
        <h2>11. Best Practices</h2>
        <ul>
          <li>Always prefer external CSS for scalability.</li>
          <li>Use classes instead of inline styles for reusability.</li>
          <li>Keep your CSS organized and commented.</li>
          <li>Use meaningful class names (BEM or descriptive names).</li>
          <li>Validate your CSS using W3C CSS Validator.</li>
        </ul>
      </section>

      {/* 12. Summary */}
      <section id="summary">
        <h2>12. Summary</h2>
        <ul>
          <li>CSS defines how HTML elements are displayed.</li>
          <li>Three integration types: Inline, Internal, and External.</li>
          <li>Selectors determine what elements to style.</li>
          <li>Cascading and specificity resolve style conflicts.</li>
          <li>External CSS enhances reusability and maintainability.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTMLCSS;
